import React, { useEffect, useState } from "react"
import moment from "moment"
import PropTypes from "prop-types"
import { FormattedMessage } from "react-intl"
import { Button } from "react-bootstrap"

import { API_STATUS } from "../../../shared_components/widgets/utils"
import PageLoadingPanel from "../../../shared_components/widgets/PageLoadingPanel"
import PageErrorPanel from "../../../shared_components/widgets/PageErrorPanel"
import { logToServer } from "../../../ErrorBoundary"
import { addNewObject, getObjectsList } from "../../../Loaders"

export default function CreateDataExportForm({ onSuccess, onError, onCancel, style = {}, intl }) {
  const datetimeDisplay = "YYYY-MM-DDTHH:mm"
  const datetimeAPI = "YYYY-MM-DDTHH:mm:ssZZ"
  const [statusInit, setStatusInit] = useState(null)
  const [reactorOptions, setReactorOptions] = useState([])
  const [formSubmitted, setFormSubmitted] = useState(null)
  const [description, setDescription] = useState("")
  const [startDatetime, setStartDatetime] = useState("")
  const [endDatetime, setEndDatetime] = useState("")
  const [reactors, setReactors] = useState([])

  const onInitialLoad = () => {
    setStatusInit(API_STATUS.LOADING)

    getObjectsList("/warehouse/sources/")
      .then((data) => {
        const reactorOptions = data.filter((d) => ["Reactor", "System"].includes(d["source_type"]))
        setStatusInit(API_STATUS.SUCCESS)
        setReactorOptions(reactorOptions)
      })
      .catch((error) => {
        logToServer(error)
        console.error(error)
        setStatusInit(API_STATUS.ERROR)
      })
  }

  useEffect(() => {
    onInitialLoad()
  }, [])

  const onFormSubmit = (event) => {
    event.preventDefault()

    // Prevent double submission of the same form
    setFormSubmitted(true)

    // Make sure earlier time is marked as the `startDatetime`
    let start, end
    if (startDatetime > endDatetime) {
      start = endDatetime
      end = startDatetime
    } else {
      start = startDatetime
      end = endDatetime
    }

    // Post data to server
    addNewObject("/lake/data-export-requests/", {
      description: description,
      reactors: reactors,
      start_datetime: moment(start).format(datetimeAPI),
      end_datetime: moment(end).format(datetimeAPI),
    })
      .then((response) => {
        // Call parent success method
        onSuccess(response.data)
      })
      .catch((responseError) => {
        // Call parent error method
        onError(responseError)
        console.error(responseError)
        logToServer(responseError)
        setFormSubmitted(false)
      })
  }

  return (
    <div>
      {statusInit === API_STATUS.LOADING ? (
        <PageLoadingPanel />
      ) : statusInit === API_STATUS.ERROR ? (
        <PageErrorPanel attemptReload={onInitialLoad} />
      ) : (
        statusInit === API_STATUS.SUCCESS && (
          <form name={"form_create_dataexportrequest"} onSubmit={onFormSubmit} style={style}>
            <div className={"one-rem-margin-bottom"}>
              <label htmlFor={"fld_description"}>
                <FormattedMessage id={"general.description"} description={"Description Field Label"} />
                *:
              </label>
              <input
                id={"fld_description"}
                name={"description"}
                type={"text"}
                className={"form-control"}
                placeholder={"Enter some description"}
                required
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className={"one-rem-margin-bottom"}>
              <label>
                <FormattedMessage id={"general.reactor"} />
                *:
              </label>
              <select
                className={"form-control"}
                multiple={true}
                name={"reactors"}
                value={reactors}
                onChange={(e) => setReactors(Array.from(e.target.selectedOptions, (item) => item.value))}
                style={{ height: "100px" }}
                required
              >
                {(reactorOptions || []).map((choice, idx) => {
                  return (
                    <option key={idx} value={choice.id}>
                      {choice.name}
                    </option>
                  )
                })}
              </select>
              <span className={"text-muted"}>
                <FormattedMessage
                  id={"general.ctrl.select.multiple"}
                  description={"Press Ctrl to Select Multiple Message"}
                />
              </span>
            </div>
            <div className={"one-rem-margin-bottom"}>
              <label>
                <FormattedMessage
                  id={"datatransfer.column.time.period"}
                  description={"Time Period Field Label"}
                />
                *:
              </label>
              <div className={"row"}>
                <div className={"col-5"}>
                  <input
                    id={"fld_start_datetime"}
                    name={"start_datetime"}
                    type={"datetime-local"}
                    className={"form-control"}
                    placeholder={"Start datetime"}
                    value={startDatetime}
                    onChange={(e) => setStartDatetime(e.target.value)}
                    required
                  />
                </div>
                <div className={"col-1"} align={"center"}>
                  -
                </div>
                <div className={"col-5"}>
                  <input
                    id={"fld_end_datetime"}
                    name={"end_datetime"}
                    type={"datetime-local"}
                    className={"form-control"}
                    placeholder={"End datetime"}
                    value={endDatetime}
                    onChange={(e) => setEndDatetime(e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>
            <div className={"one-rem-margin-bottom"}>
              <div className={"row"}>
                <div
                  className={"col-12"}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "stretch",
                  }}
                >
                  <Button
                    variant={"outline-primary"}
                    style={{ flex: "1" }}
                    onClick={(e) => {
                      e.preventDefault()
                      let now = moment()
                      setEndDatetime(now.format(datetimeDisplay))
                      setStartDatetime(now.subtract(12, "hours").format(datetimeDisplay))
                    }}
                    title={intl.formatMessage({
                      id: "datatransfer.last.twelve.hours",
                    })}
                  >
                    <FormattedMessage
                      id="datatransfer.last.twelve.hours"
                      description={"Time Period 12-hour Button Label"}
                    />{" "}
                    <span className={"fa fa-calendar-plus-o"} />
                  </Button>
                  <Button
                    variant={"outline-primary"}
                    style={{ flex: "1" }}
                    onClick={(e) => {
                      e.preventDefault()
                      let now = moment()
                      setEndDatetime(now.format(datetimeDisplay))
                      setStartDatetime(now.subtract(1, "day").format(datetimeDisplay))
                    }}
                    title={intl.formatMessage({
                      id: "datatransfer.last.one.day",
                    })}
                  >
                    <FormattedMessage
                      id="datatransfer.last.one.day"
                      description={"Time Period 1-day Button Label"}
                    />{" "}
                    <span className={"fa fa-calendar-plus-o"} />
                  </Button>
                  <Button
                    variant={"outline-primary"}
                    style={{ flex: "1" }}
                    onClick={(e) => {
                      e.preventDefault()
                      let now = moment()
                      setEndDatetime(now.format(datetimeDisplay))
                      setStartDatetime(now.subtract(1, "week").format(datetimeDisplay))
                    }}
                    title={intl.formatMessage({
                      id: "datatransfer.last.one.week",
                    })}
                  >
                    <FormattedMessage
                      id="datatransfer.last.one.week"
                      description={"Time Period 1-week Button Label"}
                    />{" "}
                    <span className={"fa fa-calendar-plus-o"} />
                  </Button>
                </div>
              </div>
            </div>
            <hr />
            <div align={"right"}>
              <Button variant={"outline-primary"} onClick={onCancel}>
                <FormattedMessage id={"general.cancelButton"} description={"Cancel Data Transfer Button"} />
              </Button>
              &nbsp;&nbsp;
              <Button variant={"primary"} type={"submit"} disabled={formSubmitted}>
                <FormattedMessage id={"general.submitButton"} description={"Submit Data Transfer Button"} />
              </Button>
            </div>
          </form>
        )
      )}
    </div>
  )
}

CreateDataExportForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  style: PropTypes.object,
  intl: PropTypes.object.isRequired,
}
